import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react'
import { LiveAuditRun } from '@trustero/trustero-api-web/lib/roadmap/roadmap_pb'
import {
  CONTROL_TEST_NAMES,
  DEFAULT_TEST_NAMES,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import log from 'loglevel'
import { useLiveAuditRuns } from '../../roadmap.hooks'

const AUDIT_PROGRESS_INTERVAL = 3000 // 3 seconds

type AuditScanProgressCtx = {
  isScanning: boolean
  getAuditRunId: (controlId: string, tests?: CONTROL_TEST_NAMES[]) => string
  registerRefetchCallback: (
    callback: () => void,
    componentId: string,
  ) => () => void
}

export const AuditScanProgress = createContext<AuditScanProgressCtx>({
  isScanning: false,
  getAuditRunId: (val: string) => val,
  registerRefetchCallback: () => () => null,
})

/**
 * The backend will return this in chronological order, so we overwrite a map key with the auditRunId of the most recent run
 *
 * The map will have keys for:
 *  - A controlId [`${controlId}`]
 *  - A controlId with a test name [`${controlId}-${testName}`]
 *  - A test name type [`${testName}`]
 *
 * ex. 'controlId123-Evidence Completeness' -> 'auditRunId123'
 *     'controlId123-Control-Policy Material Difference' -> 'auditRunId456'
 *
 * We will always use the most recent auditRunId if the control is being scanned in multiple runs
 */
const getScanControlMap = (
  liveRuns: LiveAuditRun[],
): Record<string, string> => {
  const scanControlMap: Record<string, string> = {}
  const completedControlTestMap: Record<string, string> = {}
  liveRuns.forEach((run: LiveAuditRun) => {
    const auditRunId = run.getAuditRunId()
    const controlIds = run.getControlIdsString().split(',') // Control XIDs
    let testNames = run.getControlTestsString().split(',') // Control Tests IDs were ran for
    if (testNames.length === 1 && testNames[0] === '') {
      testNames = DEFAULT_TEST_NAMES
    }

    const completedControlTests = run.getCompletedControlTestsList()
    if (
      Array.isArray(completedControlTests) &&
      completedControlTests.length > 0
    ) {
      completedControlTests.forEach((controlTest) => {
        const controlId = controlTest.getControlId() || ''
        const testName = controlTest.getTestName() || ''
        if (!controlId || !testName) return
        completedControlTestMap[`${controlId}-${testName}`] = auditRunId
      })
    }
    // If there are test names, we will append the test name to the control ID
    controlIds.forEach((controlId: string) => {
      testNames.forEach((testName: string) => {
        scanControlMap[testName] = auditRunId
        if (!completedControlTestMap[`${controlId}-${testName}`]) {
          scanControlMap[`${controlId}-${testName}`] = auditRunId
        }
      })
    })
  })
  return scanControlMap
}

export const AuditScanProgressProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const { data, isLoading, mutate, error } = useLiveAuditRuns()
  const [scanControlMap, setScanControlMap] = useState<Record<string, string>>(
    {},
  )
  const [isScanning, setIsScanning] = useState(false)
  useEffect(() => {
    if (error) {
      log.error('Error fetching live audit runs:', error)
      setIsScanning(false)
      return
    }

    if (!data || isLoading) return
    const liveRuns = data.getLiveAuditRunsList()
    setIsScanning(liveRuns.length > 0)
  }, [data, isLoading, error])
  const previousCompletedChecksRef = useRef<Record<string, string>>({})
  const refetchCallbacks = useRef(new Map<string, () => void>())
  const interval = useRef<number | null>(null)

  const stopPolling = () => {
    if (!interval.current) return
    clearInterval(interval.current)
    interval.current = null
  }

  // Helper method to get the auditRunId for a given controlId.
  // Test lists are typically 1-5 items long so this lookup is essentially O(1)
  const getAuditRunId = useCallback(
    (loadingKey: string, tests?: CONTROL_TEST_NAMES[]): string => {
      let testNames = tests
      if (!tests) {
        testNames = DEFAULT_TEST_NAMES
      }
      if (testNames && testNames.length > 0) {
        for (const test of testNames) {
          const auditRunId = scanControlMap[`${loadingKey}-${test}`]
          // If test key is found, return early otherwise continue to next test.
          if (auditRunId) return auditRunId
        }
      }
      // If no test matches, empty string will signify no current scan
      return ''
    },
    [scanControlMap],
  )

  const registerRefetchCallback = useCallback(
    (callback: () => void, componentId: string) => {
      // If the component already has a registered callback, return a no-op cleanup
      if (refetchCallbacks.current.has(componentId)) {
        return () => null
      }

      // Store the callback with the component ID as the key
      refetchCallbacks.current.set(componentId, callback)

      // Return cleanup function that removes this component's callback
      return () => {
        refetchCallbacks.current.delete(componentId)
      }
    },
    [],
  )

  useEffect(() => {
    if (!data || isLoading) return
    else if (error) log.error('Error fetching live audit runs:', error)
    const liveRuns = data.getLiveAuditRunsList()
    const scanControlMap = getScanControlMap(liveRuns)
    setScanControlMap(scanControlMap)

    // Extract current completed checks
    const currentCompletedChecks: Record<string, string> = {}
    liveRuns.forEach((run: LiveAuditRun) => {
      const completedControlTests = run.getCompletedControlTestsList()
      if (
        Array.isArray(completedControlTests) &&
        completedControlTests.length > 0
      ) {
        completedControlTests.forEach((controlTest) => {
          const controlId = controlTest.getControlId() || ''
          const testName = controlTest.getTestName() || ''
          if (!controlId || !testName) return
          currentCompletedChecks[`${controlId}-${testName}`] =
            run.getAuditRunId()
        })
      }
    })

    // Compare with previous state and trigger callbacks if there are changes
    const hasChanges =
      // Check for new keys in currentCompletedChecks
      Object.keys(currentCompletedChecks).some(
        (key) => !(key in previousCompletedChecksRef.current),
      ) ||
      // Check for keys with different values
      Object.keys(currentCompletedChecks).some(
        (key) =>
          currentCompletedChecks[key] !==
          previousCompletedChecksRef.current[key],
      ) ||
      // Check for keys that were removed
      Object.keys(previousCompletedChecksRef.current).some(
        (key) => !(key in currentCompletedChecks),
      )

    if (hasChanges) {
      refetchCallbacks.current.forEach((callback) => callback())
      previousCompletedChecksRef.current = currentCompletedChecks
    }

    const startPolling = () => {
      if (interval.current) return

      interval.current = window.setInterval(async () => {
        await mutate()
      }, AUDIT_PROGRESS_INTERVAL)
    }

    /**
     * If a user has any live runs, we will start polling
     * We will call this indefinitely while the user is in the roadmap routes and only
     * stopPolling upon leaving the page
     *
     * This will ensure that we are always up to date with the latest live runs if other team
     * members trigger new runs within the interval window. However, if there are no live runs at
     * the time of initial visit, we will not start a poll at all.
     */
    liveRuns.length && startPolling()

    return () => stopPolling()
  }, [data, isLoading, error, mutate])

  return (
    <AuditScanProgress.Provider
      value={{ getAuditRunId, registerRefetchCallback, isScanning }}
    >
      {children}
    </AuditScanProgress.Provider>
  )
}

export const useAuditScanProgress = (): AuditScanProgressCtx =>
  useContext(AuditScanProgress)
